"use client";

import type { BlockConfig } from "@/lib/registry/types";
import { useSearchParams } from "next/navigation";
import { useEffect, useState, useMemo, useRef } from "react";
import IsotopeGrid from "./isotope-grid";
import type { ReactNode } from "react";

export interface ComponentPreviewData {
  previews: {
    imageProps: any;
    demo: ReactNode | "dynamic" | null;
  };
  hasDemos: boolean;
  component?: BlockConfig; // Pass the component data for dynamic loading
}

export interface PageWrapperProps {
  components: BlockConfig[];
  initialIsScrolled?: boolean;
  alwaysScrolled?: boolean;
  initiallyFullyCollapsed?: boolean;
  preRenderedPreviews: { [key: string]: ComponentPreviewData };
}

export default function PageWrapper({
  components,
  initialIsScrolled,
  alwaysScrolled = false,
  initiallyFullyCollapsed = false,
  preRenderedPreviews,
}: PageWrapperProps) {
  const searchParams = useSearchParams();
  const [initialFilter, setInitialFilter] = useState<{
    libraries: string[];
    categories: string[];
  }>({
    libraries: [],
    categories: [],
  });

  // Reference to track if this is the first render
  const isFirstRender = useRef(true);

  // Use a stable reference to components to prevent re-renders
  const stableComponents = useRef(components);

  // Use a stable reference to previews to prevent re-renders
  const stablePreviews = useRef(preRenderedPreviews);

  useEffect(() => {
    const library = searchParams.get("library");
    const category = searchParams.get("category");

    setInitialFilter({
      libraries: library ? [library] : [],
      categories: category ? [category] : [],
    });
  }, [searchParams]);

  // Memoize the IsotopeGrid to prevent unnecessary re-renders
  const memoizedGrid = useMemo(() => {
    // Only re-render on first render or when crucial props change
    if (
      !isFirstRender.current &&
      stableComponents.current === components &&
      stablePreviews.current === preRenderedPreviews
    ) {
      return null;
    }

    // Update stable references
    stableComponents.current = components;
    stablePreviews.current = preRenderedPreviews;

    // Mark first render as complete
    isFirstRender.current = false;

    return (
      <IsotopeGrid
        items={components}
        initialFilter={initialFilter}
        initiallyScrolled={initialIsScrolled}
        alwaysScrolled={alwaysScrolled}
        initiallyFullyCollapsed={initiallyFullyCollapsed}
        preRenderedPreviews={preRenderedPreviews}
      />
    );
  }, [
    components,
    initialFilter,
    initialIsScrolled,
    alwaysScrolled,
    initiallyFullyCollapsed,
    preRenderedPreviews,
  ]);

  // If memoizedGrid is null, return the previous render
  return (
    memoizedGrid || (
      <IsotopeGrid
        items={stableComponents.current}
        initialFilter={initialFilter}
        initiallyScrolled={initialIsScrolled}
        alwaysScrolled={alwaysScrolled}
        initiallyFullyCollapsed={initiallyFullyCollapsed}
        preRenderedPreviews={stablePreviews.current}
      />
    )
  );
}
